// LT-181: admin Booking Pages management.
import React, { useEffect, useMemo, useState } from 'react';
import { BookingPage, Calendar } from '../types/calendar';
import { bookingPagesAPI, calendarsAPI } from '../services/calendarsService';

const DURATION_OPTIONS = [15, 30, 45, 60];

const PublicBaseUrl: string = (typeof window !== 'undefined' && window.location
  ? `${window.location.origin}/book/`
  : '/book/');

const BookingPagesPage: React.FC = () => {
  const [pages, setPages] = useState<BookingPage[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [form, setForm] = useState({
    calendar_id: '',
    title: '',
    duration_minutes: 30,
    advance_days: 14,
    description: '',
    location_override: '',
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pp, cc] = await Promise.all([
        bookingPagesAPI.list(),
        calendarsAPI.list({ is_active: true }),
      ]);
      setPages(pp);
      setCalendars(cc);
    } catch (e) {
      setError('Failed to load booking pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const activeCalendars = useMemo(() => calendars.filter((c) => c.is_active), [calendars]);

  const handleCreate = async () => {
    if (!form.calendar_id || !form.title) {
      setError('Pick a calendar and enter a title.');
      return;
    }
    if (form.description.length > 280) {
      setError('Description must be 280 characters or fewer.');
      return;
    }
    try {
      await bookingPagesAPI.create({
        calendar_id: form.calendar_id,
        title: form.title,
        duration_minutes: form.duration_minutes,
        advance_days: form.advance_days,
        description: form.description.trim() || null,
        location_override: form.location_override.trim() || null,
      });
      setCreating(false);
      setForm({
        calendar_id: '', title: '', duration_minutes: 30, advance_days: 14,
        description: '', location_override: '',
      });
      await load();
    } catch (e) {
      setError('Create failed');
    }
  };

  const toggleActive = async (bp: BookingPage) => {
    try {
      await bookingPagesAPI.update(bp.id, { is_active: !bp.is_active });
      await load();
    } catch {
      setError('Toggle failed');
    }
  };

  const copyUrl = async (bp: BookingPage) => {
    const url = `${PublicBaseUrl}${bp.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(bp.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Clipboard copy failed');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="booking-pages-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-slate-100">Booking Pages</h1>
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded"
          onClick={() => setCreating(true)}
          disabled={activeCalendars.length === 0}
        >
          New booking page
        </button>
      </div>
      {error && <p className="text-red-600 mb-3" role="alert">{error}</p>}

      {creating && (
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded shadow p-4 mb-4">
          <h2 className="text-lg font-medium mb-3">New booking page</h2>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col">
              <span className="text-sm text-gray-300">Calendar</span>
              <select
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                value={form.calendar_id}
                onChange={(e) => setForm({ ...form, calendar_id: e.target.value })}
              >
                <option value="">Select...</option>
                {activeCalendars.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="text-sm text-gray-300">Title</span>
              <input
                type="text"
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className="flex flex-col">
              <span className="text-sm text-gray-300">Duration (minutes)</span>
              <select
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value, 10) })}
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="text-sm text-gray-300">Advance days</span>
              <input
                type="number"
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                value={form.advance_days}
                onChange={(e) => setForm({ ...form, advance_days: parseInt(e.target.value, 10) })}
              />
            </label>
            {/* LT-511: description + location_override surface on the
                public /book/<slug> page. Both optional; back-end falls
                back to a provider-derived label when location_override
                is empty. */}
            <label className="flex flex-col col-span-2">
              <span className="text-sm text-gray-300">
                Description{' '}
                <span className="text-xs text-slate-500">
                  ({form.description.length}/280)
                </span>
              </span>
              <textarea
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                rows={2}
                maxLength={280}
                placeholder="Optional. Short blurb shown on the public booking page."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label className="flex flex-col col-span-2">
              <span className="text-sm text-gray-300">Location label</span>
              <input
                type="text"
                className="border rounded px-2 py-1 bg-slate-800 border-slate-700 text-slate-200"
                placeholder='Optional. Defaults to e.g. "Google Meet — link in invite".'
                value={form.location_override}
                onChange={(e) => setForm({ ...form, location_override: e.target.value })}
              />
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button type="button" className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleCreate}>
              Create
            </button>
            <button type="button" className="px-4 py-2 text-gray-300" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <section className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded shadow">
        {loading && <p className="text-gray-400 p-4">Loading...</p>}
        {!loading && pages.length === 0 && <p className="text-gray-400 p-4">No booking pages yet.</p>}
        {pages.length > 0 && (
          <table className="w-full text-sm text-slate-200">
            <thead>
              <tr className="text-left border-b border-white/10 text-slate-300">
                <th className="py-2 px-3 font-medium">Title</th>
                <th className="py-2 px-3 font-medium">Slug</th>
                <th className="py-2 px-3 font-medium">Duration</th>
                <th className="py-2 px-3 font-medium">Advance</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">URL</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((bp) => (
                <tr key={bp.id} className="border-b border-white/10 last:border-b-0">
                  <td className="py-2 px-3">{bp.title}</td>
                  <td className="py-2 px-3 font-mono text-xs text-slate-300">{bp.slug}</td>
                  <td className="py-2 px-3">{bp.duration_minutes}m</td>
                  <td className="py-2 px-3">{bp.advance_days}d</td>
                  <td className="py-2 px-3">
                    <button
                      type="button"
                      className={bp.is_active ? 'text-green-400' : 'text-slate-500'}
                      onClick={() => toggleActive(bp)}
                    >
                      {bp.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      className="text-blue-400 hover:text-blue-300"
                      onClick={() => copyUrl(bp)}
                    >
                      {copied === bp.id ? 'Copied!' : 'Copy URL'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default BookingPagesPage;
